/**
 * MVP renderer: HTML5 Canvas 2D. Implements the Renderer boundary so it can be
 * replaced wholesale later. It reads the world but never mutates it.
 */
import type { GameConfig, MinimapCorner } from '../../config/config';
import { clamp, lerpScalar } from '../../core/math/vec2';
import { computeMinimapBox, worldToMinimap } from '../minimap';
import type { CameraState, RenderInput, Renderer } from '../renderer';

const VOID_COLOR = '#0a0d12';
const ARENA_COLOR = '#0f1521';
const GRID_COLOR = 'rgba(255,255,255,0.045)';
const BORDER_COLOR = 'rgba(56,189,248,0.35)';
const GRID_STEP = 200;

export interface Canvas2DOptions {
  grid: boolean;
  interpolation: boolean;
  /** Whether to draw the corner minimap (FEATURES.minimap). */
  minimap: boolean;
  /** Effective corner for the minimap (the app picks a touch-safe one on mobile). */
  minimapCorner: MinimapCorner;
  /** Effective minimap box side in CSS px (smaller on mobile). */
  minimapSize: number;
}

export class Canvas2DRenderer implements Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly canvas: HTMLCanvasElement;
  private readonly cfg: GameConfig;
  private readonly opts: Canvas2DOptions;

  private cssW = 1;
  private cssH = 1;
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement, cfg: GameConfig, opts: Canvas2DOptions) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.canvas = canvas;
    this.ctx = ctx;
    this.cfg = cfg;
    this.opts = opts;
  }

  resize(cssWidth: number, cssHeight: number, devicePixelRatio: number): void {
    this.cssW = Math.max(1, cssWidth);
    this.cssH = Math.max(1, cssHeight);
    this.dpr = devicePixelRatio;
    this.canvas.width = Math.round(this.cssW * this.dpr);
    this.canvas.height = Math.round(this.cssH * this.dpr);
    this.canvas.style.width = `${this.cssW}px`;
    this.canvas.style.height = `${this.cssH}px`;
  }

  screenToWorld(screenX: number, screenY: number, camera: CameraState): { x: number; y: number } {
    return {
      x: (screenX - this.cssW / 2) / camera.zoom + camera.x,
      y: (screenY - this.cssH / 2) / camera.zoom + camera.y,
    };
  }

  render(input: RenderInput): void {
    const { ctx } = this;
    const { camera, world, alpha, colorFor } = input;
    const a = this.opts.interpolation ? alpha : 1;

    // Clear in device pixels.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = VOID_COLOR;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // World transform: device = dpr * (zoom*(w - cam) + screenCenter).
    const z = camera.zoom;
    ctx.setTransform(
      this.dpr * z,
      0,
      0,
      this.dpr * z,
      this.dpr * (this.cssW / 2 - camera.x * z),
      this.dpr * (this.cssH / 2 - camera.y * z),
    );

    // Visible world bounds (for culling + grid).
    const halfW = this.cssW / 2 / z;
    const halfH = this.cssH / 2 / z;
    const viewMinX = camera.x - halfW;
    const viewMaxX = camera.x + halfW;
    const viewMinY = camera.y - halfH;
    const viewMaxY = camera.y + halfH;

    this.drawArena(viewMinX, viewMinY, viewMaxX, viewMaxY);

    // Entities, smallest first so big blobs draw on top.
    const drawList = world.entities
      .filter((e) => e.alive)
      .sort((p, q) => p.size - q.size);

    for (const e of drawList) {
      const x = lerpScalar(e.prevPos.x, e.pos.x, a);
      const y = lerpScalar(e.prevPos.y, e.pos.y, a);
      const r = e.size;
      if (x + r < viewMinX || x - r > viewMaxX || y + r < viewMinY || y - r > viewMaxY) continue;

      const isPlayer = e.id === world.playerId;
      const fill = colorFor(e);

      // Escape-boost glow.
      if (e.boostTimer > 0) {
        ctx.beginPath();
        ctx.arc(x, y, r + 6 / z, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(244,114,182,0.18)';
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = Math.max(1.5 / z, r * 0.06);
      ctx.strokeStyle = isPlayer ? '#ffffff' : 'rgba(0,0,0,0.35)';
      ctx.stroke();

      // Name label, scaled to size but kept legible.
      const fontPx = Math.max(11 / z, r * 0.5);
      ctx.font = `${isPlayer ? '700 ' : ''}${fontPx}px Segoe UI, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.fillText(e.name, x, y);
    }

    // Minimap (screen-space, read-only second pass over world state).
    if (this.opts.minimap) this.drawMinimap(input);

    // Debug overlay (screen space).
    if (input.debug) {
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.font = '12px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(163,230,53,0.9)';
      ctx.fillText(input.debug, 12, this.cssH - 22);
    }
  }

  /**
   * Cheap minimap pass: draws the whole arena scaled into a corner box, every
   * (or only large) entity as a dot, the current camera viewport as an outline,
   * and the player as a prominent bright dot. Reads world/camera; mutates
   * nothing. O(entities) fillRects — negligible at any realistic count.
   */
  private drawMinimap(input: RenderInput): void {
    const m = this.cfg.minimap;
    const { world, camera } = input;
    const arenaW = this.cfg.world.width;
    const arenaH = this.cfg.world.height;
    const ctx = this.ctx;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const box = computeMinimapBox(
      this.cssW,
      this.cssH,
      this.opts.minimapCorner,
      this.opts.minimapSize,
      m.margin,
      arenaW,
      arenaH,
    );

    // Box background + border.
    ctx.fillStyle = m.background;
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.lineWidth = 1;
    ctx.strokeStyle = m.border;
    ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);

    // Entities (player drawn last, on top).
    for (const e of world.entities) {
      if (!e.alive || e.id === world.playerId) continue;
      const big = e.size >= m.sizeThreshold;
      if (m.showPolicy === 'threshold' && !big) continue;
      const p = worldToMinimap(e.pos.x, e.pos.y, box, arenaW, arenaH);
      const r = big ? m.threatDotPx : m.smallDotPx;
      ctx.fillStyle = big ? m.threatColor : m.smallColor;
      ctx.fillRect(p.x - r, p.y - r, r * 2, r * 2);
    }

    // Current camera viewport, clamped to the box.
    const halfW = this.cssW / 2 / camera.zoom;
    const halfH = this.cssH / 2 / camera.zoom;
    const tl = worldToMinimap(camera.x - halfW, camera.y - halfH, box, arenaW, arenaH);
    const br = worldToMinimap(camera.x + halfW, camera.y + halfH, box, arenaW, arenaH);
    const vx0 = clamp(tl.x, box.x, box.x + box.w);
    const vy0 = clamp(tl.y, box.y, box.y + box.h);
    const vx1 = clamp(br.x, box.x, box.x + box.w);
    const vy1 = clamp(br.y, box.y, box.y + box.h);
    ctx.strokeStyle = m.viewportColor;
    ctx.strokeRect(vx0, vy0, Math.max(1, vx1 - vx0), Math.max(1, vy1 - vy0));

    // Player — prominent bright dot.
    const player = world.player;
    if (player) {
      const pp = worldToMinimap(player.pos.x, player.pos.y, box, arenaW, arenaH);
      ctx.fillStyle = m.playerColor;
      ctx.beginPath();
      ctx.arc(pp.x, pp.y, m.playerDotPx, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawArena(minX: number, minY: number, maxX: number, maxY: number): void {
    const { ctx } = this;
    const { width, height } = this.cfg.world;

    // Arena fill (clamped to the actual arena rect).
    const ax0 = Math.max(0, minX);
    const ay0 = Math.max(0, minY);
    const ax1 = Math.min(width, maxX);
    const ay1 = Math.min(height, maxY);
    if (ax1 > ax0 && ay1 > ay0) {
      ctx.fillStyle = ARENA_COLOR;
      ctx.fillRect(ax0, ay0, ax1 - ax0, ay1 - ay0);
    }

    if (this.opts.grid) {
      ctx.strokeStyle = GRID_COLOR;
      ctx.lineWidth = 1 / this.cameraZoomGuess();
      ctx.beginPath();
      const startX = Math.max(0, Math.floor(minX / GRID_STEP) * GRID_STEP);
      const endX = Math.min(width, maxX);
      for (let gx = startX; gx <= endX; gx += GRID_STEP) {
        ctx.moveTo(gx, ay0);
        ctx.lineTo(gx, ay1);
      }
      const startY = Math.max(0, Math.floor(minY / GRID_STEP) * GRID_STEP);
      const endY = Math.min(height, maxY);
      for (let gy = startY; gy <= endY; gy += GRID_STEP) {
        ctx.moveTo(ax0, gy);
        ctx.lineTo(ax1, gy);
      }
      ctx.stroke();
    }

    // Arena border.
    ctx.strokeStyle = BORDER_COLOR;
    ctx.lineWidth = 6 / this.cameraZoomGuess();
    ctx.strokeRect(0, 0, width, height);
  }

  // The current transform already bakes zoom; we just need a sane line width.
  private cameraZoomGuess(): number {
    // Derived from the active transform's horizontal scale (a = dpr*zoom).
    const t = this.ctx.getTransform();
    return Math.max(0.0001, t.a / this.dpr);
  }
}
